import { useEffect, useRef, useState } from "react";
import { api, BASE, SITE_HOST, type SmsLoginInit } from "../api";
import { Logo } from "../components/Logo";
import { SmsLink } from "../components/SmsLink";
import { SmsQrCode, isMobile } from "../components/SmsQrCode";

type Mode = "login" | "signup" | "sms" | "forgot";

function friendly(err: string): string {
  switch (err) {
    case "invalid_credentials":
      return "E-posta veya parola hatalı.";
    case "email_unverified":
      return "E-postanız henüz doğrulanmadı. Gelen kutunuzu kontrol edin.";
    case "invalid_email":
      return "Geçerli bir e-posta girin.";
    case "weak_password":
      return "Parola en az 8 karakter olmalı.";
    case "missing_credentials":
      return "E-posta ve parola gerekli.";
    case "missing_phone":
      return "Geçerli bir telefon numarası girin.";
    case "terms_not_accepted":
      return "Devam etmek için sözleşmeleri kabul etmelisiniz.";
    default:
      return "Bir hata oluştu. Tekrar deneyin.";
  }
}

export function Login({
  onLogin,
  initialMode = "login",
}: {
  onLogin: (me: { id: string; email: string }) => void;
  initialMode?: Mode;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showResend, setShowResend] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  function switchMode(m: Mode) {
    setMode(m);
    setErr(null);
    setNotice(null);
    setShowResend(false);
    setForgotSent(false);
    setAcceptTerms(false);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setNotice(null);
    setShowResend(false);
    try {
      if (mode === "signup") {
        await api.signup(email.trim().toLowerCase(), password, acceptTerms);
        setNotice(
          "Hesabınız oluşturuldu. E-postanıza gönderilen doğrulama bağlantısına tıklayın.",
        );
        setMode("login");
        setPassword("");
      } else {
        const me = await api.login(email.trim().toLowerCase(), password);
        onLogin(me);
      }
    } catch (e) {
      const code = e instanceof Error ? e.message : "error";
      setErr(friendly(code));
      if (code === "email_unverified") setShowResend(true);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setErr(null);
    try {
      await api.resendVerification(email.trim().toLowerCase());
      setNotice("Doğrulama e-postası tekrar gönderildi.");
      setShowResend(false);
    } catch {
      setErr("Gönderilemedi, tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.requestPasswordReset(email.trim().toLowerCase());
      setForgotSent(true);
    } catch {
      setErr("Gönderilemedi, tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  }

  const brand = (
    <div className="login-brand">
      <Logo size={36} withDot title={SITE_HOST} />
      <div>
        <h1>Hesabınız</h1>
        <div className="muted">Giriş yapın veya ücretsiz kayıt olun</div>
      </div>
    </div>
  );

  if (mode === "forgot") {
    return (
      <div className="login-card">
        {brand}
          {forgotSent ? (
            <>
              <h2 style={{ marginTop: 8 }}>E-postanızı kontrol edin</h2>
              <p className="muted">
                <strong>{email}</strong> için bir hesap varsa parola sıfırlama bağlantısı
                gönderdik. Bağlantı 1 saat geçerlidir.
              </p>
              <button
                type="button"
                className="secondary"
                style={{ marginTop: 16 }}
                onClick={() => switchMode("login")}
              >
                Girişe dön
              </button>
            </>
          ) : (
            <form onSubmit={submitForgot}>
              <p className="muted">Hesabınıza bağlı e-postayı girin.</p>
              <label>E-posta</label>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div className="row" style={{ marginTop: 16, gap: 8 }}>
                <button type="submit" disabled={busy}>
                  {busy ? "Gönderiliyor…" : "Sıfırlama bağlantısı gönder"}
                </button>
                <button type="button" className="secondary" onClick={() => switchMode("login")}>
                  Vazgeç
                </button>
              </div>
              {err && <div className="error">{err}</div>}
            </form>
          )}
      </div>
    );
  }

  return (
    <div className="login-card">
      {brand}

      <div className="row auth-tabs" style={{ gap: 4, marginBottom: 8 }}>
          <button
            type="button"
            className={mode === "sms" ? "" : "secondary"}
            onClick={() => switchMode("sms")}
          >
            SMS ile giriş
          </button>
          <button
            type="button"
            className={mode === "login" ? "" : "secondary"}
            onClick={() => switchMode("login")}
          >
            Parola
          </button>
          <button
            type="button"
            className={mode === "signup" ? "" : "secondary"}
            onClick={() => switchMode("signup")}
          >
            Kayıt ol
          </button>
        </div>

        {mode === "sms" ? (
          <SmsForm onLogin={onLogin} />
        ) : (
          <form onSubmit={submit}>
            <label>E-posta</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <label>Parola</label>
            <input
              type="password"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {mode === "signup" && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                En az 8 karakter.
              </div>
            )}
            {mode === "signup" && (
              <label
                className="row"
                style={{ gap: 8, alignItems: "flex-start", marginTop: 12, fontSize: 13 }}
              >
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  style={{ width: "auto", marginTop: 3 }}
                />
                <span className="muted">
                  <a href={`${BASE}/veri-isleme-sozlesmesi`} target="_blank" rel="noreferrer">
                    Veri İşleme Sözleşmesi
                  </a>
                  ,{" "}
                  <a href={`${BASE}/kvkk`} target="_blank" rel="noreferrer">
                    KVKK Aydınlatma Metni
                  </a>{" "}
                  ve{" "}
                  <a href={`${BASE}/kosullar`} target="_blank" rel="noreferrer">
                    Kullanım Koşulları
                  </a>
                  ’nı okudum ve kabul ediyorum.
                </span>
              </label>
            )}

            {notice && (
              <div className="muted" style={{ marginTop: 12 }}>
                {notice}
              </div>
            )}
            {err && <div className="error">{err}</div>}
            {showResend && (
              <button
                type="button"
                className="secondary"
                style={{ marginTop: 8 }}
                onClick={resend}
                disabled={busy}
              >
                Doğrulama e-postasını tekrar gönder
              </button>
            )}

            <div style={{ marginTop: 16 }}>
              <button
                type="submit"
                disabled={busy || (mode === "signup" && !acceptTerms)}
                style={{ width: "100%" }}
              >
                {busy ? "…" : mode === "signup" ? "Kayıt ol" : "Giriş yap"}
              </button>
            </div>
            {mode === "login" && (
              <div style={{ marginTop: 12 }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    switchMode("forgot");
                  }}
                >
                  Parolanızı mı unuttunuz?
                </a>
              </div>
            )}
          </form>
        )}
    </div>
  );
}

function SmsForm({ onLogin }: { onLogin: (me: { id: string; email: string }) => void }) {
  const [phone, setPhone] = useState("");
  const [init, setInit] = useState<SmsLoginInit | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    if (!init) return;
    const target = Date.parse(init.expiresAt);
    const tick = () => setRemaining(Math.max(0, Math.round((target - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [init]);

  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (!init) return;
    let cancelled = false;
    async function check() {
      try {
        const r = await api.smsLoginStatus(init!.sessionId, init!.pollToken);
        if (cancelled) return;
        if (r.status === "verified") {
          setStatus("Doğrulandı, giriş yapılıyor…");
          onLogin({ id: r.id, email: r.email });
          return;
        }
        if (r.status === "no_account") {
          setStatus("Bu numaraya bağlı bir hesap yok. Önce kayıt olup panelden telefonunuzu ekleyin.");
          return;
        }
        if (r.status === "email_unverified") {
          setStatus("Hesabınızın e-postası henüz doğrulanmamış.");
          return;
        }
        if (r.status === "timeout") {
          setStatus("SMS gelmeden kod süresi doldu. Tekrar deneyin.");
          return;
        }
        if (r.status === "failed") {
          setStatus("Bu kod artık geçerli değil. Tekrar deneyin.");
          return;
        }
        pollRef.current = window.setTimeout(check, 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus(e instanceof Error ? e.message : "poll_failed");
      }
    }
    pollRef.current = window.setTimeout(check, 1500);
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [init, onLogin]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setStatus("");
    try {
      const r = await api.smsLoginInit(phone.trim());
      setInit(r);
      setStatus("SMS'inizi bekliyoruz…");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "init_failed");
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setInit(null);
    setStatus("");
    setErr(null);
  }

  if (init) {
    const body = `${init.code} ${init.otp}`;
    const mobile = isMobile();
    return (
      <div>
        <p className="muted">
          Kayıtlı telefonunuzdan, firmamızın SMS numarasına aşağıdaki mesajı gönderin:
        </p>
        <div className="code-display">{body}</div>
        {mobile ? (
          <SmsLink inboundNumber={init.inboundNumber} body={body} />
        ) : (
          <SmsQrCode inboundNumber={init.inboundNumber} body={body} />
        )}
        <p className="muted" style={{ marginTop: 12 }}>{remaining}s içinde geçerli.</p>
        {status && (
          <div className="muted" style={{ marginTop: 8 }}>
            {status}
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <button type="button" className="secondary" onClick={reset}>
            Farklı numara kullan
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <p className="muted">Hesabınıza bağlı telefondan SMS ile şifresiz giriş yapın.</p>
      <label>Telefon</label>
      <input
        type="tel"
        autoComplete="tel"
        placeholder="+90 5xx xxx xx xx"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        required
      />
      <div style={{ marginTop: 16 }}>
        <button type="submit" disabled={busy} style={{ width: "100%" }}>
          {busy ? "İsteniyor…" : "Kod gönder"}
        </button>
      </div>
      {err && <div className="error">{friendlyInit(err)}</div>}
    </form>
  );
}

function friendlyInit(err: string): string {
  if (err === "missing_phone") return "Geçerli bir telefon numarası girin.";
  return "Bir hata oluştu. Tekrar deneyin.";
}
